/* eslint-disable @next/next/no-img-element */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton } from '@material-ui/core';
import { useUser } from '@auth0/nextjs-auth0/client';
import Cookies from 'js-cookie';
import { useOnboardingStyles } from '../../onboarding/OnboardingDesignWrapper';
import MetaTagGenerator from '../../shared/MetaTagGenerator';

import { transformAuth0IntoQwaSession } from '../../../auth/session';
import { CONTENT_AI_USER_TYPE, ISPDomains, SUBSCRIPTION_STATUS } from '../../../constants/common';
import { useRouter } from 'next/router';
import {
  COMPONENT_NAMES,
  SCREEN_NAMES,
  segmentEventApi,
  setUserAttribute,
  trackAnalyticsEvent,
} from '../../../constants/analyticsMeta';
import CloseIcon from '@material-ui/icons/Close';
import { useView } from '../../../utility/services/uiHelpers';
import { DescribeQuestion } from './DescribeQuestion';
import { setCookie } from './tools-common';
import { VerifyEmailComponent } from './VerifyEmailComponent';
import { BuyOnePass, BuyOnePassSuccess } from './BuyOnePass';
import axios from 'axios';
import { DAY_PASS_LITE_PLAN_ID, getAiToolsUrl, USER_CLASSIFICATION } from '../../../utility/common';
import { get } from 'lodash';
import { useSession } from '../../../utility/services/customHooks';
import UpgradeToolsPlan from '../../onboarding/upgradeToolsPlan';
import { actionTypes } from '../../../constants/actionTypes';
import { useDispatch, useSelector } from 'react-redux';
import { signOut } from '../../../utility/services/authActions';
import FreeToolsOnboarding from './FreeToolsOnboarding';
import QuattrLoader from '../../shared/QuattrLoader';
import { useSignUpStyles } from '../../../../pages/get-started';

const useStyles = makeStyles((theme) => ({
  // all common classes for all tools
  '@global': {},
  body: {
    backgroundColor: 'white',
  },
  textAreaInput: {
    borderRadius: '4px',
    border: '1px solid rgba(29, 53, 87, 0.50)',
    color: 'rgba(29, 53, 87, 0.50)',
    fontFamily: 'Source Sans Pro',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 400,
    width: 'calc(100% - 0px)',
    margin: '16px 0px',
    '&.no-border': {
      border: 'unset',
    },
    '&.no-border .MuiOutlinedInput-notchedOutline': {
      border: '1px solid rgba(29, 53, 87, 0.50)',
    },
    '.MuiInputBase-root .Mui-disabled': {
      background: '#F6F7F9',
      color: '#1D3557', // Change 'red' to any color you like
    },
    '&.MuiFormControl-root .Mui-disabled': {
      background: '#F6F7F9',
      color: '#1D3557', // Change 'green' to any color you like
    },
    '&.background-gray': {
      background: '#d3d3d37d',
    },
  },
  disabledInput: {
    '& textarea::selection': {
      backgroundColor: 'transparent',
      color: 'inherit',
    },
    '& textarea': {
      WebkitUserSelect: 'none' /* Safari */,
      MozUserSelect: 'none' /* Firefox */,
      msUserSelect: 'none' /* IE10+/Edge */,
      userSelect: 'none' /* Standard */,
    },
  },
  dropDown: {
    borderRadius: '4px !important',
    color: '#1D3557',
    fontFamily: 'Source Sans Pro',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '108%',
    '& .MuiOutlinedInput-root': {
      '&:hover fieldset': {
        border: '1px solid rgba(29, 53, 87, 0.50)', // Color when hovered
      },
      '&.Mui-focused fieldset': {
        border: '1px solid rgba(29, 53, 87, 0.50)', // Color when focused
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: '1px solid rgba(29, 53, 87, 0.50)', // Default border color
    },
  },
  dropDownOption: {
    color: '#1D3557',
    fontFamily: 'Source Sans 3',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 400,
    display: 'flex',
    justifyContent: 'space-between',
    // lineHeight: '10%',
  },
  toolsYellowCTA: {
    color: '#2A332E',
    textAlign: 'center',
    fontFamily: 'Source Sans Pro',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 600,
    borderRadius: '4px',
    background: '#FFC72C',
    textTransform: 'capitalize',
    lineHeight: 'unset',
    padding: '16px 30px',
    '&:hover': {
      backgroundColor: '#FFC72C',
    },
    boxShadow: 'unset',
    // [theme.breakpoints.up('sm')]: {
    //   background: 'red',
    // },
    [theme.breakpoints.down('xs')]: {
      // background: 'green',
      width: '100%',
    },
  },
  toolsOutlinedCTA: {
    borderRadius: '4px',
    border: '1px solid #1D3557',
    background: '#fff',
    '&:hover': {
      backgroundColor: '#fff',
    },
  },
  unlockIcon: {
    width: '19px',
    height: '19px',
    fill: '#1D3557CC',
  },
  skipCaptcha: {
    color: '#1D3557',
    fontFamily: '"Source Sans Pro"',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '145%',
    margin: '10px 10px 10px 0px',
  },
  skipCaptchaLink: {
    color: '#51D497',
    fontFamily: '"Source Sans Pro"',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '108%',
    textDecorationLine: 'underline',
    cursor: 'pointer',
  },
  tableWrap: {
    boxShadow: 'none',
    border: 'none',
    borderBottom: '1px solid rgba(33, 33, 33, 0.14)',
    borderTop: '1px solid rgba(33, 33, 33, 0.14)',
    marginTop: '16px',
  },
  tableHead: {
    color: '#1D3557',
    fontFamily: 'Source Sans Pro',
    fontSize: '10px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '16px',
    letterSpacing: '0.2px',
    textTransform: 'capitalize',
    padding: '8px',
  },
  tableRow: {
    color: '#1D3557',
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.2px',
    textTransform: 'capitalize',
  },
  tableCell: {
    padding: '8px !important',
    textAlign: 'left',
  },

  container: {
    position: 'relative',
    height: 'auto',
    // marginTop: '16px',
  },
  loader: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 9999,
  },
  overlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    background: 'radial-gradient(circle, white, white 10%, rgba(255, 255, 255, 0.5) 90%, transparent)',
    zIndex: 9998,
  },
  signUpOverlay: {
    display: 'flex',
    background: 'rgba(255, 255, 255, 0.95)',
    justifyContent: 'center',
    alignItems: 'flex-start',
    position: 'fixed',
    width: '100vw',
  },
  signUpOverlayDashboard: {
    display: 'flex',
    background: 'rgba(255, 255, 255, 0.95)',
    justifyContent: 'center',
    alignItems: 'flex-start',
    position: 'absolute',
    zIndex: 99,
  },
  sectionSubHeading: {
    color: '#1D3557',
    fontFamily: 'Poppins',
    fontSize: '20px',
    fontStyle: 'normal',
    lineHeight: 'normal',
    fontWeight: 700,
    margin: '16px 0px',
    padding: '0px',
  },
  buttonGroup: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
    gap: '10px',
    [theme.breakpoints.down(500)]: {
      // targeting screens smaller than sm
      flexDirection: 'column-reverse',
      alignItems: 'center',
    },
  },
  creditsShown: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    [theme.breakpoints.down(440)]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      marginTop: '20px',
    },
  },
  freeCreditsShown: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    [theme.breakpoints.down(440)]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      marginTop: '20px',
    },
  },
  recaptcha: {
    display: 'flex',
    marginTop: '16px',
    flexDirection: 'row',
    [theme.breakpoints.down(440)]: {
      flexDirection: 'column',
    },
  },
  creditsContainer: {
    minWidth: '188px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginTop: '16px',
  },
  button: {
    marginRight: '16px',
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(1), // provide spacing between buttons on small screens
      marginRight: '0px',
    },
    '&:last-child': {
      marginRight: 0, // Override margin-right for the last button
    },
  },
  signUpContainer: {
    maxWidth: '420px',
    marginTop: '100px',
  },
  signUpContainerDashboard: {
    display: 'flex',
    justifyContent: 'center',
    maxWidth: '420px',
    marginTop: '100px',
  },
  thankyouPopup: {
    background: '#F6F7F9',
    width: '679px',
    padding: '24px',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    color: '#1D3557',
    textAlign: 'center',
    fontFamily: 'Poppins',
    fontSize: '20px',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '160%',
    marginTop: '150px',
    [theme.breakpoints.down('xs')]: {
      // targeting screens smaller than sm
      width: '100%',
      padding: '8px',
    },
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: '208px',
    color: '#000',
  },
  dayPassInfoWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    alignSelf: 'stretch',
    flexWrap: 'nowrap',
    flexShrink: 0,
    position: 'relative',
    minWidth: '0',
  },
  upgradePlanWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    alignSelf: 'stretch',
    flexWrap: 'nowrap',
    flexShrink: 0,
    position: 'relative',
    minWidth: '0',
  },
  dayPassCreditLeft: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    flexShrink: 0,
    position: 'relative',
    height: '30px',
    padding: '16px 16px 16px 16px',
    background: 'rgba(255, 199, 44, 0.2)',
    border: '1px solid #ffc72c',
    zIndex: 1,
    borderRadius: '4px',
    marginTop: '10px',
  },
  dayPassPlanBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'nowrap',
    flexShrink: 0,
    gap: '10px',
    position: 'relative',
    height: '22px',
    padding: '16px 16px 16px 16px',
    background: 'rgba(81, 212, 151, 0.1)',
    border: '1px solid #51d497',
    zIndex: 4,
    borderRadius: '4px',
    marginTop: '10px',
  },
  upgradePlanContainer: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'right',
  },
  upgradePlanText: {
    color: '#51D497',
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '108%',
    textDecorationLine: 'underline',
    cursor: 'pointer',
  },
  upgradePlanDescription: {
    color: '#1D3557',
    textAlign: 'right',
    fontFamily: 'Source Sans Pro',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 400,
  },
  requiredCredits: {
    fontFamily: 'IBM Plex Sans',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    marginLeft: '5px',
  },
  requiredCreditsOnlyText: {
    fontFamily: 'IBM Plex Sans',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 600,
    marginLeft: '5px',
  },
  creditsConsumedText: {
    fontFamily: 'IBM Plex Sans',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 400,
  },
  orText: {
    fontWeight: 500,
    width: '100%',
    textAlign: 'center',
    marginBottom: '16px',
  },
  creditsRemaining: {
    fontSize: '14px',
  },
  additionInstructionsText: {
    [theme.breakpoints.down(500)]: {
      fontSize: '12px',
      margin: 0,
      padding: 0,
    },
  },
  outputActionBtnWrapper: {
    display: 'flex',
    flexDirection: 'row',
    [theme.breakpoints.down('xs')]: {
      gap: '10px',
      flexDirection: 'column',
    },
  },
}));
type toolsPayload = {
  email?: string;
};

export const calculateRows = (value) => {
  if (!value) {
    return 8;
  }
  const lineBreaks = (value.match(/\n/g) || []).length;
  return lineBreaks;
};

export const CONTINUE_USING_TEXT = 'continue using Quattr';

export const ToolComponent = ({ children, classes, showSignUpPage, isQwaDashboard }) => {
  const componentRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const queryParams = new URLSearchParams(window.parent.location.search);
    const verifyEmail = queryParams.get('verifyEmail');
    const buyPass = queryParams.get('buyPass');
    const state = queryParams.get('state');
    const id = queryParams.get('id');
    const handleResize = () => {
      if (componentRef.current && !showSignUpPage && !verifyEmail && !buyPass && !(state && id)) {
        window.parent.postMessage(
          {
            action: 'resizeToolSize',
            height: componentRef.current.offsetHeight + 70,
          },
          '*'
        );
      }
    };

    // Initialize ResizeObserver
    const resizeObserver = new ResizeObserver(handleResize);

    // Start observing the component
    if (componentRef.current) {
      resizeObserver.observe(componentRef.current);
    }

    // Perform the resize action once initially
    handleResize();

    // Cleanup function to unobserve when the component unmounts
    return () => {
      if (componentRef.current) {
        resizeObserver.unobserve(componentRef.current);
      }
    };
  }, [showSignUpPage]);
  useEffect(() => {
    if (componentRef.current) {
      if (window) {
        window.parent.postMessage({ action: 'resizeToolSize', height: componentRef.current.offsetHeight }, '*');
      }
    }
  }, []); // Empty array ensures this only runs once on mount

  useEffect(() => {
    // removed default background color for the body
    // removed intercom launcher button
    document.body.classList.add(classes.body);
    if (window.Intercom) {
      window.Intercom('update', { hide_default_launcher: isQwaDashboard ? false : true });
    }
    return () => {
      document.body.classList.remove(classes.body);
    };
  }, []);
  // Free-tool inputs, generated text, and uploaded images must stay out of replay:
  // data-private for LogRocket, data-hj-suppress for Hotjar (loaded through GTM).
  return (
    <div ref={componentRef} data-private data-hj-suppress>
      {children}
    </div>
  );
};

const ToolsStyleWrapper = ({ component: Component, isQwaDashboard = false, isAiToolUser = false }) => {
  const classes = useStyles();
  const [showSignUpPage, setShowSignUpPage] = useState('');
  const [toolName, setToolName] = useState('');
  const [toolCategory, setToolCategory] = useState('');
  const [isCorporateEmail, setIsCorporateEmail] = useState(false);
  const toolMeta = useMemo(() => {
    return {
      tool_name: toolName,
      tool_category: toolCategory,
      isQwaDashboard: isQwaDashboard,
    };
  }, [toolName, toolCategory]);
  const [openPopup, setOpenPopup] = React.useState(false);
  const { session } = useSession();
  const hasActiveSubscription =
    session &&
    [
      SUBSCRIPTION_STATUS.SUBSCRIPTION_ACTIVATED,
      SUBSCRIPTION_STATUS.SUBSCRIPTION_REACTIVATED,
      SUBSCRIPTION_STATUS.SUBSCRIPTION_RESUMED,
    ].includes(get(session, 'user.subscription_status', ''));
  const [showSuccessEmailVerification, setShowSuccessEmailVerification] = React.useState(false);
  const [showVerifyEmailPopup, setShowVerifyEmailPopup] = React.useState(false);
  const [showBuyOnePass, setShowBuyOnePass] = React.useState(false);
  const [isRedirectToWebflow, setIsRedirectToWebflow] = React.useState(false);
  const [verifiedEmailHere, setVerifiedEmailHere] = React.useState(false);
  const [dayPassLiteUsage, setDayPassLiteUsage] = React.useState(0);
  const [freeToolsUsage, setFreeToolsUsage] = React.useState(0);
  const [showFreeToolsUsage, setShowFreeToolsUsage] = React.useState(false);
  const [showFreeToolsSignUp, setShowFreeToolsSignUp] = React.useState(false);
  const [dayPassLiteUsageLoading, setDayPassLiteUsageLoading] = React.useState(false);
  const [showBuyOnePassSuccess, setShowBuyOnePassSuccess] = React.useState(false);
  const [creditsRemainingState, setCreditsRemainingState] = React.useState(0);
  const [usagePerDay, setUsagePerDay] = React.useState(0);
  const isMobile = useView('mobileOnly');
  const dispatch = useDispatch();
  const [showMinimumCreditsNeededMsg, setShowMinimumCreditsNeededMsg] = React.useState(false);
  const dayPassCreditsRemaining = useSelector((state) => state?.root?.dayPassCreditsRemaining || 0);

  let use_locked_grace = Cookies.get('use_locked_grace');
  let is_locked_grace_used = Cookies.get('is_locked_grace_used');
  let qttr_tools_daypass_auth = Cookies.get('qttr_tools_daypass_auth');
  let has_questions_grace = Cookies.get('has_questions_grace');
  // let has_email_verification_lock_grace = Cookies.get('has_email_verification_lock_grace');
  // let has_email_verification_usage_grace = Cookies.get('has_email_verification_usage_grace');
  let qttr_tools_auth = Cookies.get('qttr_tools_auth');

  // let buyone_request_id = Cookies.get('buyone_request_id');

  const isClickedOnLockedOption = showSignUpPage && showSignUpPage !== CONTINUE_USING_TEXT;
  const isReachedMaxLimit = showSignUpPage === CONTINUE_USING_TEXT;
  const [isUseLockedGraceAvailable, setIsUseLockedGraceAvailable] = useState(use_locked_grace || '');
  // toolsAuth wont be alway a jwt
  const [toolsAuth, setToolsAuth] = useState(qttr_tools_auth || '');
  const [dayPassAuth, setDayPassAuth] = useState(qttr_tools_daypass_auth || '');
  // const [buyOneRequestId, setBuyOneRequestId] = useState(buyone_request_id || '');
  const [isLockedGraceUsedOnce, setIsLockedGraceUsedOnce] = useState(is_locked_grace_used || '');
  const [isFromUnlockOption, setIsFromUnlockOption] = useState(false);
  const [dashboardRedirect, setDashboardRedirect] = useState(false);

  const onboardingStyles = useOnboardingStyles();
  const signupStyles = useSignUpStyles();
  const componentRef = useRef<HTMLDivElement | null>(null);
  const verifySignupCompRef = useRef<HTMLDivElement | null>(null);
  const { user } = useUser();
  const qwaSession = user ? transformAuth0IntoQwaSession(user) : undefined;
  const router = useRouter();
  // const isFromUnlockOption = Object.keys(router.query).includes('unlock-options');

  const isDomainAdded = Boolean(user && qwaSession && qwaSession.user.groups.length && qwaSession.selected_schema);
  const isExpiredOrCancelledSubscription = Boolean(
    user &&
      qwaSession &&
      qwaSession.user.subscription_status &&
      (qwaSession.user.subscription_status === SUBSCRIPTION_STATUS.SUBSCRIPTION_CANCELLED ||
        qwaSession.user.subscription_status === SUBSCRIPTION_STATUS.SUBSCRIPTION_PAUSED)
  );

  const scrollToTop = () => {
    window.parent.scrollTo({
      top: 0,
      behavior: 'smooth', // This makes the scrolling smooth. Remove it if you want instant scroll.
    });
  };

  const handleClose = () => {
    setOpenPopup(false);
  };

  const resetLockedGrace = () => {
    setIsUseLockedGraceAvailable('');
    setIsLockedGraceUsedOnce('true');
    setCookie('is_locked_grace_used', 'true', true);
  };

  const decodeJwt = (token) => {
    try {
      if (token) {
        const [headerEncoded, payloadEncoded, signatureEncoded] = token?.split('.');
        const payload = JSON.parse(atob(payloadEncoded));
        return payload;
      }
    } catch (error) {
      console.error('decodeJwt=>');
    }
  };

  const sendEmailOnFreeAccountCreation = async (qttr_tools_auth) => {
    try {
      if (qttr_tools_auth) {
        const [headerEncoded, payloadEncoded, signatureEncoded] = qttr_tools_auth.split('.');

        // Decode the base64-encoded payload
        const payload = JSON.parse(atob(payloadEncoded));
        const UserEmail = payload.email;
        const UserFirstName = payload.firstName;

        const res = await axios.post(
          '/api/join-content-ai/email-on-free-tools-account-creation',
          {
            email: UserEmail,
            firstName: UserFirstName,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
      } else {
        console.error('auth cookie not found');
      }
    } catch (error) {
      console.error('Error :: ');
    }
  };

  const decodeJWTAndTrackUser = (token) => {
    try {
      const [headerEncoded, payloadEncoded, signatureEncoded] = token.split('.');

      // Decode the base64-encoded payload
      const payload = JSON.parse(atob(payloadEncoded));
      if (payload && payload.email) {
        if (window && window.analytics) {
          window.analytics.identify(payload.email, {
            email: payload.email,
            name: payload.name,
            schema: '',
            instance_type: 'Content AI',
            user_type: CONTENT_AI_USER_TYPE,
            company_name: '',
            last_verified_at: new Date().toISOString(),
            email_verified_with: 'google',
          });
        }
      }

      trackAnalyticsEvent({
        eventName: 'Verified Email with Google',
        eventFor: 'btn',
        payload: {
          label: 'Verified Email with Google',
          ...payload,
          component_name: COMPONENT_NAMES.contentai_ai_tools,
          screen_name: SCREEN_NAMES.contentai_ai_tools,
          ...toolMeta,
        },
      });
    } catch (error) {
      console.error('decodeJWTAndTrackUser=>');
    }
  };

  useEffect(() => {
    if (session?.user?.email && !isQwaDashboard && qttr_tools_auth) {
      // Function to hide the Intercom launcher
      const hideIntercomLauncher = () => {
        const launcher: any = document.querySelector('.intercom-lightweight-app-launcher');
        if (launcher) {
          launcher.style.display = 'none';
        }
      };

      // Call the function to hide the launcher
      hideIntercomLauncher();

      setDashboardRedirect(true);
      setShowFreeToolsSignUp(false);
      const dashboardRedirectUrl = getAiToolsUrl(window?.parent?.location?.pathname)
        ? getAiToolsUrl(window?.parent?.location?.pathname)
        : '/app/ai-tools-home';
      window.parent.location.pathname = dashboardRedirectUrl;
    } else if (!session?.user?.email && (qttr_tools_auth || toolsAuth) && !isQwaDashboard) {
      setShowFreeToolsSignUp(true);
    }
  }, [session?.user?.email, isQwaDashboard, qttr_tools_auth, toolsAuth]);

  useEffect(() => {
    if (showSignUpPage && componentRef.current) {
      console.log({ action: 'resizeToolSize', height: componentRef.current.offsetHeight });
      if (window) {
        window.parent.postMessage({ action: 'resizeToolSize', height: 750 }, '*');
      }
    }
  }, [showSignUpPage]);

  useEffect(() => {
    if (componentRef.current && (showFreeToolsSignUp || showVerifyEmailPopup)) {
      console.log({ action: 'resizeToolSize', height: componentRef.current.offsetHeight });
      if (window) {
        window.parent.postMessage({ action: 'resizeToolSize', height: 750 }, '*');
      }
    }
  }, [showFreeToolsSignUp, showVerifyEmailPopup]);

  // useEffect(() => {
  //   if (!tool_usr_desc && isDescriberQuestionAnswered == 'Student or Academia') {
  //     setOpenPopup(true);
  //   }
  // }, [isDescriberQuestionAnswered]);

  // useEffect(() => {
  //   if (isStudent && isReachedMaxLimit) {
  //     setOpenPopup(true);
  //   }
  // }, [showSignUpPage, isReachedMaxLimit, isStudent]);

  useEffect(() => {
    if (showSignUpPage && !showVerifyEmailPopup && isMobile) {
      window.parent.postMessage({ action: 'resizeToolSize', height: 1079 }, '*');
    }
  }, [showSignUpPage]);

  // useEffect(() => {
  //   if (showSignUpPage && isDescriberQuestionAnswered && !openPopup && !isStudent) {
  //     const UrlToRedirect = isClickedOnLockedOption ? '/get-started?unlock-options' : '/get-started?signup-now';
  //     localStorage.setItem('previousUrl', window?.parent?.location?.href);
  //     setShowSignUpPage('');
  //     trackAnalyticsEvent({
  //       eventName: 'Prompted to Sign Up',
  //       counterName: 'Prompted to Sign Up',
  //       isAddCount: true,
  //       user,
  //       isAnonymousUser: !user,
  //       payload: {
  //         label: 'Prompted to Sign Up',
  //         reason: isClickedOnLockedOption ? 'Clicked on Locked Option' : 'Reached your maximum usage limit',
  //         component_name: COMPONENT_NAMES.contentai_ai_tools,
  //         screen_name: SCREEN_NAMES.contentai_ai_tools,
  //         ...toolMeta,
  //       },
  //     });
  //     window.parent.postMessage({ action: 'redirectURL', url: UrlToRedirect }, '*');
  //   }
  // }, [showSignUpPage, isDescriberQuestionAnswered, openPopup, isStudent]);

  const scheduleEmailFeeUsageResume = useCallback(
    async (toolMeta) => {
      try {
        if (session?.user?.email) {
          const payload = {
            email: session?.user?.email,
            firstname: session?.user?.name?.split(' ')?.[0] || '',
            lastname: session?.user?.name?.split(' ')?.[1] || '',
            ...toolMeta,
            href: `${window.parent.location.origin}${window.parent.location.pathname}`,
            isQwaDashboard: isQwaDashboard,
          };
          const config = {
            method: 'POST',
            url: `/api/join-content-ai/free-tools-resume-reminder`,
            data: payload,
          };
          const response = await axios.request(config);
          if (response?.data) {
            console.log('scheduled free usage resume email');
          }
        } else {
          console.error('No cookie auth available');
        }
      } catch (error) {
        console.error('Error, scheduling email');
      }
    },
    [isQwaDashboard, session?.user?.email, session?.user?.name]
  );

  useEffect(() => {
    // if (isClickedOnLockedOption || showSignUpPage) {
    //   if (!toolsAuth) {
    //     const url = new URL(window.parent.location.href);
    //     url.searchParams.set('verifyEmail', 'true');
    //     if (isClickedOnLockedOption) {
    //       url.searchParams.set('unlock-options', 'true');
    //       setIsFromUnlockOption(true);
    //     }
    //     window.parent.history.pushState({}, '', url);
    //     setShowSignUpPage('');
    //     setShowVerifyEmailPopup(true);
    //   } else if (isStudent && (isClickedOnLockedOption || isReachedMaxLimit)) {
    //     setOpenPopup(true);
    //   }
    // } else
    if (!toolsAuth && !session && (isReachedMaxLimit || isClickedOnLockedOption)) {
      setShowVerifyEmailPopup(true);
    } else if (
      (toolsAuth || (session && !hasActiveSubscription)) &&
      (isReachedMaxLimit || isClickedOnLockedOption) &&
      session?.user?.userType !== USER_CLASSIFICATION.QWA_TOOLS
    ) {
      setShowBuyOnePass(true);
    } else if (session && (isReachedMaxLimit || isClickedOnLockedOption)) {
      if (session?.user?.userType === USER_CLASSIFICATION.QWA_TOOLS) {
        setShowBuyOnePass(true);
      }
    }
  }, [toolsAuth, session, hasActiveSubscription, isClickedOnLockedOption, isReachedMaxLimit, freeToolsUsage]);

  useEffect(() => {
    if (
      isQwaDashboard &&
      session?.user?.email &&
      isReachedMaxLimit &&
      toolMeta?.tool_name &&
      toolMeta?.tool_category &&
      (!session?.user?.planId ||
        session?.user?.qwa_tools_subscription_status === SUBSCRIPTION_STATUS.SUBSCRIPTION_CANCELLED) &&
      !qttr_tools_daypass_auth
    ) {
      scheduleEmailFeeUsageResume(toolMeta);
    }
  }, [
    isReachedMaxLimit,
    toolMeta,
    showSignUpPage,
    qttr_tools_daypass_auth,
    isQwaDashboard,
    session?.user?.email,
    session?.user?.planId,
    session?.user?.qwa_tools_subscription_status,
    scheduleEmailFeeUsageResume,
  ]);

  useEffect(() => {
    if (showSignUpPage && openPopup && isClickedOnLockedOption && !isLockedGraceUsedOnce) {
      trackAnalyticsEvent({
        eventName: 'Unlocked an Option',
        payload: {
          label: 'Unlocked an Option',
          component_name: COMPONENT_NAMES.contentai_ai_tools,
          screen_name: SCREEN_NAMES.contentai_ai_tools,
          option: showSignUpPage,
          ...toolMeta,
        },
      });
    }
  }, [openPopup, isClickedOnLockedOption, isLockedGraceUsedOnce]);
  useEffect(() => {
    if (window) {
      let timeoutId;
      // Use URLSearchParams to parse the query parameters
      const searchParams = window.parent.location.search;
      if (!searchParams) {
        // fallback if search params are not found on first render
        timeoutId = setTimeout(() => {
          const searchParams = window.parent.location.search;
          if (!searchParams) {
            console.log('searchParams missing even in timeout');
          }
          const queryParams = new URLSearchParams(searchParams);
          const verifyEmail = queryParams.get('verifyEmail');
          const auth = queryParams.get('auth');
          const isSuccess = queryParams.get('success');
          const isSuccessStateFromChargebee = queryParams.get('state');
          const isFromChargebee = queryParams.get('id');
          const isBuyPass = queryParams.get('buyPass');
          if (isSuccessStateFromChargebee && isFromChargebee) {
            if (qttr_tools_auth || session) {
              setShowBuyOnePassSuccess(true);
            } else {
              setShowVerifyEmailPopup(true);
            }
          } else if (verifyEmail && !qttr_tools_auth) {
            setShowVerifyEmailPopup(true);
          } else if (auth == 'googleoauth' && qttr_tools_auth && isSuccess) {
            // setShowVerifyEmailPopup(true);
            // setShowSuccessEmailVerification(true);
            setVerifiedEmailHere(true);
            sendEmailOnFreeAccountCreation(qttr_tools_auth);
            decodeJWTAndTrackUser(qttr_tools_auth);
            // setShowBuyOnePass(true)
          } else if (isBuyPass) {
            if (qttr_tools_auth || session) {
              setShowBuyOnePass(true);
            } else {
              setShowVerifyEmailPopup(true);
            }
          }
          const FromUnlockOptions = queryParams.get('unlock-options');
          if (FromUnlockOptions) {
            setIsFromUnlockOption(true);
          }
        }, 500);
      }
      const queryParams = new URLSearchParams(window.parent.location.search);

      const verifyEmail = queryParams.get('verifyEmail'); // Replace 'example' with your parameter name
      const isSuccess = queryParams.get('success');

      const isSuccessStateFromChargebee = queryParams.get('state');
      const isFromChargebee = queryParams.get('id');
      const isBuyPass = queryParams.get('buyPass');
      const auth = queryParams.get('auth');

      if (isSuccessStateFromChargebee && isFromChargebee) {
        if (qttr_tools_auth || session) {
          setShowBuyOnePassSuccess(true);
        } else {
          setShowVerifyEmailPopup(true);
        }
      } else if (verifyEmail && !qttr_tools_auth) {
        setShowVerifyEmailPopup(true);
      } else if (auth == 'googleoauth' && qttr_tools_auth && isSuccess) {
        // setShowVerifyEmailPopup(true);
        // setShowSuccessEmailVerification(true);
        setVerifiedEmailHere(true);
        sendEmailOnFreeAccountCreation(qttr_tools_auth);
        decodeJWTAndTrackUser(qttr_tools_auth);
        setShowBuyOnePass(true);
      } else if (isBuyPass) {
        if (qttr_tools_auth || session) {
          setShowBuyOnePass(true);
        } else {
          setShowVerifyEmailPopup(true);
        }
      }

      const FromUnlockOptions = queryParams.get('unlock-options');
      if (FromUnlockOptions) {
        setIsFromUnlockOption(true);
      }
      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }
  }, [session]);
  useEffect(() => {
    if ((showVerifyEmailPopup || showBuyOnePass || showBuyOnePassSuccess) && verifySignupCompRef.current) {
      window.parent.postMessage(
        {
          action: 'resizeToolSize',
          height: verifySignupCompRef.current.offsetHeight + 700,
        },
        '*'
      );
    }
  }, [showVerifyEmailPopup, verifySignupCompRef.current]);

  const checkIfUserHasPurchasedDayPass = async () => {
    try {
      let qttr_tools_auth = Cookies.get('qttr_tools_auth');
      const qttr_tools_daypass_auth =
        dayPassAuth || (!session?.user?.userType && Cookies.get('qttr_tools_daypass_auth'));

      if (!qttr_tools_daypass_auth && !showBuyOnePassSuccess && (qttr_tools_auth || session)) {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        let payloadBody = {};
        if (qttr_tools_auth) {
          const [headerEncoded, payloadEncoded, signatureEncoded] = qttr_tools_auth.split('.');
          const payload = JSON.parse(atob(payloadEncoded));
          payloadBody = {
            email: payload.email,
            firstName: payload.firstName,
            lastName: payload.lastName,
            timezone,
            isQwaDashboard: isQwaDashboard,
          };
        } else {
          payloadBody = {
            email: session?.user.email,
            firstName: session?.user.name?.split(' ')[0] || '',
            lastName: session?.user.name?.split(' ')[1] || '',
            timezone,
            isQwaDashboard: isQwaDashboard,
          };
        }
        // Decode the base64-encoded payload
        const res = await axios.post('/api/join-content-ai/verify-day-pass-purchased', payloadBody, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (res?.data?.data && res?.data?.data?.id) {
          const qttr_tools_daypass_auth = Cookies.get('qttr_tools_daypass_auth');
          if (qttr_tools_daypass_auth) {
            setDayPassAuth(qttr_tools_daypass_auth);
            handleResetUsage(qttr_tools_daypass_auth);
          }
        }
      }
    } catch (error) {
      console.error('checkIfUserHasPurchasedDayPass::');
    }
  };

  const handleResetUsage = async (dayPassAuthNew = '') => {
    try {
      setDayPassLiteUsageLoading(true);
      let qttr_tools_auth = Cookies.get('qttr_tools_auth');
      const tempDayPassAuth = dayPassAuth || dayPassAuthNew;
      const dayPassTokenDetails = tempDayPassAuth ? decodeJwt(tempDayPassAuth) : {};
      const isDayPassLite = DAY_PASS_LITE_PLAN_ID === dayPassTokenDetails.plan_id;
      if ((qttr_tools_auth || session) && isDayPassLite) {
        let payloadBody = {};

        if (qttr_tools_auth) {
          const [headerEncoded, payloadEncoded, signatureEncoded] = qttr_tools_auth.split('.');
          const payload = JSON.parse(atob(payloadEncoded));
          payloadBody = {
            email: payload.email,
            firstName: payload.firstName,
            lastName: payload.lastName,
          };
        } else {
          payloadBody = {
            email: session?.user.email,
            firstName: session?.user.name?.split(' ')[0] || '',
            lastName: session?.user.name?.split(' ')[1] || '',
          };
        }
        // Decode the base64-encoded payload
        const res = await axios.post('/api/join-content-ai/get-user-day-pass-usage', payloadBody, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (res.data.data.remaining !== undefined) {
          setDayPassLiteUsage(res.data.data.remaining);
          dispatch({
            type: actionTypes.SET_REMAINING_DAY_PASS_CREDITS,
            dayPassCreditsRemaining: res?.data?.data?.remaining || 0,
          });
          if (res?.data?.data?.remaining === 0) {
            qttr_tools_daypass_auth = '';
            setDayPassAuth('');
            setCookie('qttr_tools_daypass_auth', '', false, true);
            if (
              ![SUBSCRIPTION_STATUS.SUBSCRIPTION_CANCELLED].includes(
                session?.user?.qwa_tools_subscription_status || ''
              ) &&
              creditsRemainingState === 0
            ) {
              getPaidToolPlansCreditsRemaining(session?.user?.email, session?.user?.userType, session?.user?.planId);
            }
          }
        }
      } else {
        if (
          session?.user?.email &&
          session?.user?.userType &&
          session?.user?.planId &&
          ![SUBSCRIPTION_STATUS.SUBSCRIPTION_CANCELLED].includes(session?.user?.qwa_tools_subscription_status as any)
        ) {
          getPaidToolPlansCreditsRemaining(session?.user?.email, session?.user?.userType, session?.user?.planId);
        } else if ((showFreeToolsUsage || (!isQwaDashboard && qttr_tools_auth)) && !dayPassAuth) {
          setShowFreeToolsUsage(true);
          getFreeToolsRemainingUsage();
        }
      }
    } catch (error) {
      console.error('handleGetCheckoutPage');
    } finally {
      setDayPassLiteUsageLoading(false);
    }
  };
  useEffect(() => {
    handleResetUsage();
    checkIfUserHasPurchasedDayPass();
  }, [session]);

  useEffect(() => {
    if (
      dayPassCreditsRemaining === 0 &&
      session?.user &&
      ![SUBSCRIPTION_STATUS.SUBSCRIPTION_CANCELLED].includes(session?.user?.qwa_tools_subscription_status as any) &&
      !dayPassAuth &&
      !qttr_tools_daypass_auth
    ) {
      handleResetUsage();
    }
  }, [session, dayPassAuth, dayPassCreditsRemaining, qttr_tools_daypass_auth]);

  const isCorporateAndValidEmail = (e) => {
    const str = e.toLowerCase();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const allowedPersonalEmails = new Set([
      'maheshauti00@gmail.com',
      'sanjoli1097@gmail.com',
      'bmmc1990@gmail.com',
      // Add more allowed emails here
    ]);

    // Check for ISP domains
    const emailDomain = str.split('@')[1]; // Extract domain from email
    if (ISPDomains.includes(emailDomain)) {
      return false; // ISP domain found
    }

    const nonCorporateDomains = [
      'gmail.com',
      'outlook.com',
      'hotmail.com',
      'yahoo.com',
      'me.com',
      'icloud.com',
      'msn.com',
      'live.com',
      'proton.me',
      '.edu',
      'ac.in',
    ];

    if (!emailPattern.test(str)) return false;
    if (allowedPersonalEmails.has(str)) return true;

    return !nonCorporateDomains.some((domain) => {
      if (domain === '.edu') {
        return str.endsWith('.edu') || str.endsWith('.edu.');
      }
      return str.endsWith(domain);
    });
  };

  const getFreeToolsRemainingUsage = async () => {
    try {
      let qttr_tools_daypass_auth = Cookies.get('qttr_tools_daypass_auth');
      if ((qttr_tools_auth && !qttr_tools_daypass_auth) || session) {
        let payloadBody: toolsPayload = {};
        if (session) {
          payloadBody = {
            email: session?.user?.email,
          };
        } else if (qttr_tools_auth) {
          const [headerEncoded, payloadEncoded, signatureEncoded] = qttr_tools_auth.split('.');
          const payload = JSON.parse(atob(payloadEncoded));
          payloadBody = {
            email: payload.email,
          };
        } else {
          console.error('user has not verified its email or its dayPass is active');
        }

        const res = await axios.post('/api/join-content-ai/get-free-tools-usage', payloadBody, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (res.data.data.remaining !== undefined) {
          setFreeToolsUsage(res.data.data.remaining);
          if (
            !session?.user.email ||
            ((!session?.user?.planId ||
              session?.user?.qwa_tools_subscription_status === SUBSCRIPTION_STATUS.SUBSCRIPTION_CANCELLED) &&
              [USER_CLASSIFICATION.QWA_TOOLS, USER_CLASSIFICATION.CONTENT_AI].includes(session?.user?.userType as any))
          ) {
            setCreditsRemainingState(res.data.data.remaining);
            setUsagePerDay(res.data.data.usageLimitPerDay);
            dispatch({
              type: actionTypes.SET_CREDITS_REMAINING,
              creditsRemaining: res?.data?.data?.remaining,
            });
            let isCorp = false;
            if (payloadBody?.email) {
              isCorp = isCorporateAndValidEmail(payloadBody?.email);
              setIsCorporateEmail(isCorp);
            }
            dispatch({
              type: actionTypes.SET_CREDITS_REQUIRED_PER_DAY,
              creditsUsagePerDay: isCorp ? 10 : 7,
            });
          }
        }
      } else {
        console.log('user has not verified its email or its dayPass is active');
      }
    } catch (error) {
      console.log('error');
    }
  };
  useEffect(() => {
    const userType = session?.user?.userType;
    const hasActiveToolsSubscription =
      Boolean(
        [
          SUBSCRIPTION_STATUS.SUBSCRIPTION_ACTIVATED,
          SUBSCRIPTION_STATUS.SUBSCRIPTION_CREATED,
          SUBSCRIPTION_STATUS.SUBSCRIPTION_REACTIVATED,
          SUBSCRIPTION_STATUS.SUBSCRIPTION_RESUMED,
        ]?.includes(session?.user?.qwa_tools_subscription_status as any)
      ) || false;
    const hasActiveContentAiSubscription =
      Boolean(
        [
          SUBSCRIPTION_STATUS.SUBSCRIPTION_ACTIVATED,
          SUBSCRIPTION_STATUS.SUBSCRIPTION_REACTIVATED,
          SUBSCRIPTION_STATUS.SUBSCRIPTION_RESUMED,
        ]?.includes(session?.user?.subscription_status as any)
      ) || false;

    const showFreeCredits =
      (!hasActiveToolsSubscription && userType === USER_CLASSIFICATION.QWA_TOOLS) ||
      (userType === USER_CLASSIFICATION.CONTENT_AI && !hasActiveToolsSubscription && !hasActiveContentAiSubscription);

    if (((showBuyOnePass && freeToolsUsage !== 0) || verifiedEmailHere) && !dayPassAuth && !session?.user?.email) {
      getFreeToolsRemainingUsage();
      setShowFreeToolsUsage(true);
    } else if (showFreeCredits && !dayPassAuth) {
      getFreeToolsRemainingUsage();
      setShowFreeToolsUsage(true);
    }
  }, [showBuyOnePass, verifiedEmailHere, session, dayPassAuth, freeToolsUsage]);

  const sendSlackMessageIfFails = async (error, email) => {
    try {
      const isPlainObject = (obj) => {
        return Object.prototype.toString.call(obj) === '[object Object]';
      };
      const fields = [
        {
          title: 'Error Message',
          value: error.message || 'Unknown error',
          short: false,
        },
        {
          title: 'Stack Trace',
          value: `\`\`\`${error.stack || 'No stack trace available'}\`\`\``,
          short: false,
        },
        {
          title: 'User Email',
          value: email || 'No email provided',
          short: false,
        },
        {
          title: 'Method',
          value: 'getPaidToolPlansCreditsRemaining',
          short: false,
        },
      ];

      if (error.response) {
        fields.push({
          title: 'Response Status',
          value: `${error.response.status}`,
          short: true,
        });

        if (isPlainObject(error.response.data)) {
          fields.push({
            title: 'Response Data',
            value: `\`\`\`${JSON.stringify(error.response.data, null, 2)}\`\`\``,
            short: false,
          });
        } else if (typeof error.response.data === 'string' && !error.response.data.startsWith('<')) {
          fields.push({
            title: 'Response Data',
            value: `\`\`\`${error.response.data}\`\`\``,
            short: false,
          });
        }
      } else if (error.request) {
        fields.push({
          title: 'Request',
          value: `\`\`\`${JSON.stringify(error.request, null, 2)}\`\`\``,
          short: false,
        });
      }
      const payload = {
        text: `Error getting usageLimitPerDay and remaining credits`,
        attachments: [
          {
            color: 'danger',
            fields: fields,
          },
        ],
      };
      await axios.post('/api/post_slack_message', { payload: payload });
    } catch (error: any) {
      const payload = {
        text: `Error sending slack message on Error getting usageLimitPerDay and remaining credits`,
        attachments: [
          {
            color: 'danger',
            fields: [
              {
                title: 'Error Message',
                value: error.message || 'Unknown error',
                short: false,
              },
            ],
          },
        ],
      };
      console.error('Error :: sending slack message on get all daypass link fail');
      await axios.post('/api/post_slack_message', { payload: payload });
    }
  };

  const getPaidToolPlansCreditsRemaining = useCallback(
    async (email, userType, planId) => {
      try {
        const payloadBody = {
          email,
          userType,
          planId,
        };
        const result = await axios.post('/api/join-content-ai/get-paid-tools-plan-usage', payloadBody, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (result?.data?.data) {
          setCreditsRemainingState(result.data.data.remaining);
          setUsagePerDay(result.data.data.usageLimitPerDay);
          dispatch({
            type: actionTypes.SET_CREDITS_REMAINING,
            creditsRemaining: result?.data?.data?.remaining,
          });
          dispatch({
            type: actionTypes.SET_CREDITS_REQUIRED_PER_DAY,
            creditsUsagePerDay: result?.data?.data?.usageLimitPerDay,
          });
        }
      } catch (error: any) {
        console.error('getCreditsRemaining :: Error ::');
        if (
          [403, 401].includes(error?.response?.status) &&
          ['Requires Authentication', 'Access Denied!!!'].includes(error?.response?.data)
        ) {
          await signOut({ callbackUrl: '/login', redirect: true });
          trackAnalyticsEvent({
            eventName: 'Logged out due to session expiry',
            payload: {
              label: 'Logged out due to session expiry',
              methodCalled: 'getCreditsRemaining::tools',
            },
          });
        } else {
          sendSlackMessageIfFails(error, email);
          trackAnalyticsEvent({
            eventName: 'error getting remaining credits',
            payload: {
              label: 'error getting remaining credits',
              email: session?.user?.email,
              error: error?.message,
              userType: session?.user.userType,
              planId: session?.user.planId,
              referencePath: window.location.href,
              errorDetails: error,
            },
          });
        }
      }
    },
    [session?.user?.email, session?.user?.userType, session?.user?.planId]
  );

  const handleContinue = () => {
    setShowBuyOnePassSuccess(false);
    setShowBuyOnePass(false);
    setShowSignUpPage('');
    setIsRedirectToWebflow(false);
    setShowVerifyEmailPopup(false);

    const queryParams = new URLSearchParams(window.parent.location.search);
    queryParams.delete('id');
    queryParams.delete('state');
    queryParams.delete('plan_id');
    window.parent.history.pushState({}, '', `${window.parent.location.pathname}?${queryParams}`);
    window?.parent?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    const qttr_tools_daypass_auth = Cookies.get('qttr_tools_daypass_auth');
    if (qttr_tools_daypass_auth) {
      setDayPassAuth(qttr_tools_daypass_auth);
      handleResetUsage(qttr_tools_daypass_auth);
    } else {
      checkIfUserHasPurchasedDayPass();
    }
  };

  const handleWait = () => {
    window?.parent?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    setShowBuyOnePass(false);
    setShowSignUpPage('');
    setOpenPopup(false);
    setVerifiedEmailHere(false);
    setShowSuccessEmailVerification(false);
    setShowVerifyEmailPopup(false);
    setIsRedirectToWebflow(false);
    setIsFromUnlockOption(false);
    setShowMinimumCreditsNeededMsg(false);
    const queryParams = new URLSearchParams(window.parent.location.search);
    queryParams.delete('verifyEmail');
    queryParams.delete('success');
    queryParams.delete('auth');
    queryParams.delete('unlock-options');
    window.parent.history.pushState({}, '', `${window.parent.location.pathname}?${queryParams}`);
  };

  const handleCloseSignUp = () => {
    setShowSuccessEmailVerification(false);
    setShowVerifyEmailPopup(false);
    setShowFreeToolsSignUp(false);
    setShowBuyOnePass(false);
    setShowSignUpPage('');
    setOpenPopup(false);
    setVerifiedEmailHere(false);
    setShowSuccessEmailVerification(false);
    setIsRedirectToWebflow(false);
    setIsFromUnlockOption(false);
    setShowMinimumCreditsNeededMsg(false);
    const queryParams = new URLSearchParams(window.parent.location.search);
    queryParams.delete('verifyEmail');
    queryParams.delete('success');
    queryParams.delete('auth');
    queryParams.delete('unlock-options');
    window.parent.history.pushState({}, '', `${window.parent.location.pathname}?${queryParams}`);
  };
  // console.log('toolsWrapperStatus', {
  //   isLockedGraceUsedOnce,
  //   openPopup,
  //   showSignUpPage,
  //   isReachedMaxLimit,
  //   isClickedOnLockedOption,
  //   use_locked_grace,
  //   isFromUnlockOption,
  //   showBuyOnePass,
  //   showBuyOnePassSuccess,
  //   showVerifyEmailPopup,
  //   dayPassLiteUsage,
  //   freeToolsUsage,
  //   session,
  // });

  if (showFreeToolsSignUp) {
    return (
      <>
        <div ref={componentRef} className={`${classes.signUpContainer}`} style={{ width: 'unset', maxWidth: 'unset' }}>
          <FreeToolsOnboarding
            toolMeta={toolMeta}
            isQwaDashboard={isQwaDashboard}
            isCookiePresent={true}
            handleCloseSignUp={handleCloseSignUp}
          />
        </div>
      </>
    );
  } else if (dashboardRedirect) {
    return (
      <div className="account-created-loader-wrapper">
        <QuattrLoader
          isTransparent={true}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            padding: '30px 20px',
          }}
        />
      </div>
    );
  }

  const dayPassTokenDetails = dayPassAuth ? decodeJwt(dayPassAuth) : {};
  const isDayPassLite = DAY_PASS_LITE_PLAN_ID === dayPassTokenDetails.plan_id;
  return (
    <>
      <div>
        {dayPassAuth ? (
          <div className={classes.dayPassInfoWrapper}>
            {isDayPassLite && dayPassLiteUsage > 0 ? (
              <button className={classes.dayPassCreditLeft}>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    flexShrink: 0,
                    flexBasis: 'auto',
                    position: 'relative',
                    height: '19px',
                    color: '#1d3557',
                    fontFamily: 'Source Sans Pro',
                    fontSize: '16px',
                    fontWeight: 700,
                    lineHeight: '17.28px',
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                    zIndex: 2,
                  }}
                >
                  {dayPassLiteUsage < 10 ? `0${dayPassLiteUsage}` : dayPassLiteUsage}
                </span>
                <div
                  style={{
                    flexShrink: 0,
                    position: 'relative',
                    width: '118px',
                    height: '24px',
                    fontFamily: 'Source Sans Pro',
                    fontSize: '18px',
                    fontWeight: 400,
                    lineHeight: '19.44px',
                    textAlign: 'left',
                    textOverflow: 'initial',
                    whiteSpace: 'nowrap',
                    zIndex: 3,
                  }}
                >
                  <span
                    style={{
                      position: 'relative',
                      color: '#1d3557',
                      fontFamily: 'Source Sans Pro',
                      fontSize: '12px',
                      fontWeight: 400,
                      lineHeight: '19.44px',
                      textAlign: 'left',
                    }}
                  >
                    /20 {` `} credits remaining
                  </span>
                </div>
              </button>
            ) : (
              <span> </span>
            )}
            {dayPassAuth && isDayPassLite && dayPassLiteUsage > 0 ? (
              <button className={classes.dayPassPlanBanner}>
                <span
                  style={{
                    flexGrow: 1,
                    flexShrink: 0,
                    flexBasis: 'auto',
                    position: 'relative',
                    minWidth: '0',
                    height: '13px',
                    color: '#51d497',
                    fontFamily: 'Source Sans Pro',
                    fontSize: '12px',
                    fontWeight: 700,
                    lineHeight: '12.96px',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    zIndex: 5,
                  }}
                >
                  {'DAY PASS - ACTIVE'}
                </span>
              </button>
            ) : (
              <span> </span>
            )}
          </div>
        ) : null}
        {showFreeToolsUsage && qttr_tools_auth && !(session && session?.user?.email) ? (
          <div className={classes.upgradePlanWrapper}>
            {showFreeToolsUsage ? (
              <div className={classes.upgradePlanContainer}>
                <span
                  className={classes.upgradePlanText}
                  onClick={() => {
                    setShowBuyOnePass(true);
                    setIsRedirectToWebflow(true);
                    trackAnalyticsEvent({
                      eventName: 'clicked on Upgrade Plan',
                      payload: {
                        label: 'clicked on Upgrade Plan',
                        isQwaDashboard: isQwaDashboard,
                      },
                    });
                  }}
                >
                  Upgrade Plan{' '}
                  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="11" viewBox="0 0 10 11" fill="none">
                    <path
                      d="M7.91667 8.41667H2.08333V2.58333H5V1.75H2.08333C1.86232 1.75 1.65036 1.8378 1.49408 1.99408C1.3378 2.15036 1.25 2.36232 1.25 2.58333V8.41667C1.25 8.63768 1.3378 8.84964 1.49408 9.00592C1.65036 9.1622 1.86232 9.25 2.08333 9.25H7.91667C8.375 9.25 8.75 8.875 8.75 8.41667V5.5H7.91667V8.41667ZM5.83333 1.75V2.58333H7.32917L3.23333 6.67917L3.82083 7.26667L7.91667 3.17083V4.66667H8.75V1.75H5.83333Z"
                      fill="#51D497"
                    />
                  </svg>
                </span>
                <span className={classes.upgradePlanDescription}>to get more daily credits</span>
              </div>
            ) : (
              <span> </span>
            )}
          </div>
        ) : showFreeToolsUsage &&
          !dayPassAuth &&
          isQwaDashboard &&
          ['QWA_TOOLS', 'CONTENT_AI'].includes(session?.user?.userType as any) ? (
          <div className={`${classes.upgradePlanWrapper} mt-10`}>
            {showFreeToolsUsage ? (
              <div className={classes.upgradePlanContainer}>
                <span
                  className={classes.upgradePlanText}
                  onClick={() => {
                    trackAnalyticsEvent({
                      eventName: 'clicked on Upgrade Plan',
                      payload: {
                        label: 'clicked on Upgrade Plan',
                        isQwaDashboard: isQwaDashboard,
                      },
                    });
                    router.push('/app/add-credits');
                  }}
                >
                  Upgrade Plan{' '}
                  <svg xmlns="http://www.w3.org/2000/svg" width="10" height="11" viewBox="0 0 10 11" fill="none">
                    <path
                      d="M7.91667 8.41667H2.08333V2.58333H5V1.75H2.08333C1.86232 1.75 1.65036 1.8378 1.49408 1.99408C1.3378 2.15036 1.25 2.36232 1.25 2.58333V8.41667C1.25 8.63768 1.3378 8.84964 1.49408 9.00592C1.65036 9.1622 1.86232 9.25 2.08333 9.25H7.91667C8.375 9.25 8.75 8.875 8.75 8.41667V5.5H7.91667V8.41667ZM5.83333 1.75V2.58333H7.32917L3.23333 6.67917L3.82083 7.26667L7.91667 3.17083V4.66667H8.75V1.75H5.83333Z"
                      fill="#51D497"
                    />
                  </svg>
                </span>
                <span className={classes.upgradePlanDescription}>to get more daily credits</span>
              </div>
            ) : (
              <span> </span>
            )}
          </div>
        ) : null}
      </div>
      <ToolComponent classes={classes} showSignUpPage={showSignUpPage} isQwaDashboard={isQwaDashboard}>
        {/* <Script
        src={`https://www.google.com/recaptcha/api.js`}
        strategy="afterInteractive"
        onLoad={() => {
          console.log('reCAPTCHA script has loaded!');
        }}
      /> */}
        <MetaTagGenerator forceNoIndex noFollow noCanonical />
        {showVerifyEmailPopup && !(toolsAuth || session) ? (
          <div
            ref={verifySignupCompRef}
            className={`${classes.overlay} center-align qtools-overlay-active ${
              isQwaDashboard ? classes.signUpOverlayDashboard : classes.signUpOverlay
            }`}
          >
            <div className={`${classes.signUpContainer}`} style={{ width: 'unset', maxWidth: 'unset' }}>
              {showSuccessEmailVerification ? (
                <div className={classes.thankyouPopup}>
                  <IconButton
                    aria-label="close"
                    className={classes.closeButton}
                    onClick={() => {
                      setShowSuccessEmailVerification(false);
                      setShowVerifyEmailPopup(false);
                      setShowSignUpPage('');
                      setIsRedirectToWebflow(false);
                      setShowMinimumCreditsNeededMsg(false);
                      const queryParams = new URLSearchParams(window.parent.location.search);
                      queryParams.delete('verifyEmail');
                      queryParams.delete('success');
                      queryParams.delete('auth');
                      queryParams.delete('unlock-options');
                      window.parent.history.pushState({}, '', `${window.parent.location.pathname}?${queryParams}`);
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                  {isFromUnlockOption || isClickedOnLockedOption
                    ? 'Thank you for your response. You may now use the locked option 1 time'
                    : 'Thank you for your response. You may now use the tool to generate 5 more outputs'}
                </div>
              ) : (
                <div ref={componentRef}>
                  <FreeToolsOnboarding toolMeta={toolMeta} />
                </div>
              )}
            </div>
          </div>
        ) : null}
        {showBuyOnePass ? (
          <div
            ref={verifySignupCompRef}
            className={`${classes.overlay} center-align qtools-overlay-active ${
              isQwaDashboard ? classes.signUpOverlayDashboard : classes.signUpOverlay
            }`}
          >
            {isQwaDashboard ? (
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                style={{ top: '50px' }}
                onClick={() => {
                  handleWait();
                  trackAnalyticsEvent({
                    eventName: 'Clicked on Cross',
                    payload: {
                      label: 'Clicked on Cross',
                      ...(toolMeta || {}),
                    },
                  });
                }}
              >
                <CloseIcon />
              </IconButton>
            ) : null}

            <div className={`${classes.signUpContainer}`} style={{ width: '100%', maxWidth: 'unset' }}>
              <BuyOnePass
                isQwaDashboard={isQwaDashboard}
                setShowBuyOnePass={setShowBuyOnePass}
                setOpenPopup={setOpenPopup}
                handleWait={handleWait}
                toolMeta={toolMeta || {}}
                verifiedEmailHere={verifiedEmailHere}
                isReachedMaxLimit={isReachedMaxLimit}
                isFromUnlockOption={(showSignUpPage && showSignUpPage !== CONTINUE_USING_TEXT) || isFromUnlockOption}
                isFreeUsageAndDayPassExist={freeToolsUsage === 0 && toolsAuth && !dayPassAuth}
                isRedirectToWebflow={isRedirectToWebflow}
                showMinimumCreditsNeededMsg={showMinimumCreditsNeededMsg}
              />
            </div>
          </div>
        ) : showBuyOnePassSuccess ? (
          <div
            ref={verifySignupCompRef}
            className={`${classes.overlay} center-align qtools-overlay-active ${
              isQwaDashboard ? classes.signUpOverlayDashboard : classes.signUpOverlay
            }`}
          >
            <div
              className={`${isQwaDashboard ? classes.signUpContainerDashboard : classes.signUpContainer}`}
              style={{ width: '100%', maxWidth: 'unset' }}
            >
              <BuyOnePassSuccess
                isQwaDashboard={isQwaDashboard}
                handleContinue={handleContinue}
                toolMeta={toolMeta || {}}
              />
            </div>
          </div>
        ) : null}
        {showSignUpPage && !isQwaDashboard && !showVerifyEmailPopup && !showBuyOnePass && !showBuyOnePassSuccess ? (
          <div
            ref={componentRef}
            className={`${classes.overlay} center-align qtools-overlay-active ${
              isQwaDashboard ? classes.signUpOverlayDashboard : classes.signUpOverlay
            }`}
          >
            <div className={`${classes.signUpContainer}`} style={{ maxWidth: 'unset' }}>
              {/* {(isDescriberQuestionAnswered && !isUseLockedGraceAvailable && isReachedMaxLimit) && (
              <h3 className={`${onboardingStyles.subText} center-align`}>
                {' '}
                {!user ? (
                  <>
                    <b className="red">
                      You have reached your maximum usage limit. <br />
                      You can resume again in 4 hours{' '}
                    </b>{' '}
                    <br /> <br /> <b>OR</b> <br />
                  </>
                ) : null}
              </h3>
            )} */}
              {
                // !isDescriberQuestionAnswered ? (
                //   <DescribeQuestion
                //     setIsDescriberQuestionAnswered={setIsDescriberQuestionAnswered}
                //     setIsUseLockedGraceAvailable={setIsUseLockedGraceAvailable}
                //     isReachedMaxLimit={isReachedMaxLimit}
                //     isClickedOnLockedOption={isClickedOnLockedOption}
                //     handleOpen={(value) => setShowVerifyEmailPopup(value)}
                //     toolMeta={toolMeta}
                //   />
                // ) :
                openPopup ? (
                  <div className={classes.thankyouPopup}>
                    <IconButton
                      aria-label="close"
                      className={classes.closeButton}
                      onClick={() => {
                        if (isReachedMaxLimit || !isUseLockedGraceAvailable || use_locked_grace) {
                          setShowSignUpPage('');
                        }
                        handleClose();
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                    {((isLockedGraceUsedOnce && isClickedOnLockedOption) ||
                      (toolsAuth && isClickedOnLockedOption && !isUseLockedGraceAvailable) ||
                      isReachedMaxLimit) && (
                      <div className="red">
                        You’ve reached your maximum usage limit
                        <br />
                        {isReachedMaxLimit ? (
                          <span style={{ fontSize: '20px' }}>You can resume again in 8 hours</span>
                        ) : null}
                      </div>
                    )}
                  </div>
                ) : null
              }
              {/* {!openPopup && !showVerifyEmailPopup && (
                <p className={`${onboardingStyles.subText} center-align`} style={{ color: '#1D3557' }}>
                  <a
                    className={`${onboardingStyles.link}`}
                    style={{ color: '#1D3557' }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowSignUpPage('');
                      if (!isDescriberQuestionAnswered) {
                        trackAnalyticsEvent({
                          eventFor: 'button',
                          buttonText: '',
                          counterName: 'Job role question skipped',
                          isAddCount: true,
                          isAnonymousUser: !user,
                          payload: {
                            label: 'Job role question skipped',
                            component_name: COMPONENT_NAMES.contentai_ai_tools,
                            screen_name: SCREEN_NAMES.contentai_ai_tools,
                            ...toolMeta,
                          },
                        });
                      } else {
                        trackAnalyticsEvent({
                          eventFor: 'button',
                          buttonText: '',
                          counterName: 'Clicked on Go back & continue',
                          isAddCount: true,
                          isAnonymousUser: !user,
                          payload: {
                            label: 'Clicked on Go back & continue',
                            component_name: COMPONENT_NAMES.contentai_ai_tools,
                            screen_name: SCREEN_NAMES.contentai_ai_tools,
                            ...toolMeta,
                          },
                        });
                      }
                      scrollToTop();
                    }}
                    href={'/'}
                  >
                    Go back & continue
                  </a>
                </p>
              )} */}
            </div>
          </div>
        ) : null}
        <Component
          classes={classes}
          creditsRemaining={
            Boolean(qttr_tools_daypass_auth) && dayPassCreditsRemaining > 0
              ? dayPassCreditsRemaining
              : creditsRemainingState
          }
          usagePerDay={usagePerDay}
          isCreditsShown={
            [
              SUBSCRIPTION_STATUS.SUBSCRIPTION_ACTIVATED,
              SUBSCRIPTION_STATUS.SUBSCRIPTION_CREATED,
              SUBSCRIPTION_STATUS.SUBSCRIPTION_REACTIVATED,
              SUBSCRIPTION_STATUS.SUBSCRIPTION_RESUMED,
            ].includes(session?.user?.qwa_tools_subscription_status as any) &&
            !qttr_tools_daypass_auth &&
            session?.user.userType !== USER_CLASSIFICATION.AI_SEO_SUITE
          }
          user={user}
          handleResetUsage={handleResetUsage}
          // key={`${isUseLockedGraceAvailable || Boolean(qttr_tools_daypass_auth)}`}
          enableLockedOption={isUseLockedGraceAvailable || Boolean(qttr_tools_daypass_auth) || isAiToolUser}
          resetLockedGrace={isUseLockedGraceAvailable ? resetLockedGrace : undefined}
          hasValidSubscription={isDomainAdded && !isExpiredOrCancelledSubscription}
          setShowSignUpPage={(e, toolMeta) => {
            setShowSignUpPage(e);
            if (toolMeta) {
              if (toolMeta.tool_name && toolMeta.tool_category) {
                setToolName(toolMeta.tool_name);
                setToolCategory(toolMeta.tool_category);
              }
            }
          }}
          isQwaDashboard={isQwaDashboard}
          setShowBuyOnePass={setShowBuyOnePass}
          setIsRedirectToWebflow={setIsRedirectToWebflow}
          setShowMinimumCreditsNeededMsg={setShowMinimumCreditsNeededMsg}
          setShowVerifyEmailPopup={setShowVerifyEmailPopup}
          freeToolsUsage={freeToolsUsage}
          showFreeToolsUsage={
            showFreeToolsUsage &&
            !dayPassAuth &&
            ((qttr_tools_auth && !(session && session?.user?.email)) ||
              !session?.user?.planId ||
              ['QWA_TOOLS', 'CONTENT_AI'].includes(session?.user?.userType as any))
          }
          isCorpEmail={isCorporateEmail}
        />
      </ToolComponent>
    </>
  );
};
export default ToolsStyleWrapper;
